import { useState } from 'react';
import reactTextareaAutosize from 'react-textarea-autosize';

import { DirectorySetPromptEndpoint } from '@core/types/types.endpoint.directory';
import { Button } from '@frontend/components/ui/button';
import { Textarea } from '@frontend/components/ui/textarea';
import { useDirectoryUpdater } from '@frontend/shared/hooks/shared.hook.useDirectoryUpdater';
import { useDirectoryTeam } from '@frontend/shared/hooks/useDirectory';
import { useSubmitter } from '@frontend/shared/hooks/useSubmitter';
import { callApi } from '@frontend/shared/util/util.callApi';

export function SettingsPrompt({
  title,
  promptName,
  defaultPrompt,
  description,
  isReadOnly = false,
}: {
  title: string;
  description: string;
  promptName:
    | 'prompt_classify_ignored'
    | 'prompt_documentation_approved'
    | 'prompt_documentation_output'
    | 'prompt_answer_style'
    | 'prompt_context';
  defaultPrompt: string;
  isReadOnly?: boolean;
}) {
  const team = useDirectoryTeam();
  const [editing, setEditing] = useState(false);
  const updater = useDirectoryUpdater();
  const currentPrompt = team?.[promptName] ?? defaultPrompt;
  const [prompt, setPrompt] = useState(currentPrompt);

  const updatePrompt = useSubmitter(async () => {
    let promptToSet = prompt.trim();
    if (promptToSet === currentPrompt) {
      return;
    }
    if (promptToSet === defaultPrompt) {
      promptToSet = '';
    }

    const [, res] = await callApi<DirectorySetPromptEndpoint>(
      '/directory/setPrompt',
      {
        name: promptName,
        prompt: promptToSet,
      },
    );

    updater(res);
    setEditing(false);
  });

  return (
    <div>
      <div className="flex items-center">
        <div className="w-full">
          <h3 className="text-xs font-semibold">{title}</h3>
          <p className="mt-1 text-xs font-medium text-gray-500">
            {description}
          </p>
        </div>
        <Button
          size="sm"
          className="ml-2 shrink-0 px-3"
          disabled={editing || isReadOnly}
          style={{
            opacity: editing ? 0 : 1,
            visibility: editing ? 'hidden' : 'visible',
          }}
          variant="default"
          onClick={() => !isReadOnly && setEditing(true)}
        >
          Edit prompt
        </Button>
      </div>

      {!editing && (
        <p
          className={`mt-1 whitespace-pre-line py-1 text-sm font-normal italic ${
            currentPrompt ? 'text-inherit' : 'text-gray-400'
          }`}
        >
          {currentPrompt || 'No custom prompt set'}
        </p>
      )}
      {editing && (
        <Textarea
          autoFocus
          // @ts-expect-error - reactTextareaAutosize typing issue
          as={reactTextareaAutosize}
          value={prompt}
          className="text-sm leading-[21px]"
          onChange={(e) => setPrompt(e.target.value)}
        />
      )}
      {editing && (
        <div className="mt-2 flex">
          <Button
            size="sm"
            variant="default"
            onClick={updatePrompt.submit}
            disabled={prompt === currentPrompt || updatePrompt.isSubmitting}
          >
            {updatePrompt.isSubmitting ? 'Saving...' : 'Save'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="ml-2"
            onClick={() => {
              setEditing(false);
              setPrompt(currentPrompt);
            }}
          >
            Cancel
          </Button>

          {prompt !== defaultPrompt && (
            <Button
              size="sm"
              variant="outline"
              className="ml-auto"
              onClick={() => setPrompt(defaultPrompt)}
            >
              Reset to default
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
